import { useAddSalesContext } from "features/leads/components/addSalesModal/addSalesContext/addSalesContext"
import ProjectDetailBar from "../projectDetailBar/projectDetailBar"
import { Button, Group, Legend, notification, SelectInput, Text } from "components/common"
import ProvisionalSaleModal from "../provisionalSaleModal"
import { useMemo, useRef } from "react"
import { Form } from "antd"
import { t } from "i18next"
import { flag } from "features/leads/components/addSalesModal/utils"
import { IconText } from "components/custom"
import theme from "utils/themeVars"

const ProjectDetails = ({ rules }) => {
  const provisionalFormRef = useRef()
  const { addSalesApis, stateManager, form, updateStateManager, updateIsEditable, formWatchers, lead } =
    useAddSalesContext()
  const { project, unit } = formWatchers
  const {
    leadSalesInfo,
    projects,
    getActiveBookingPlatforms,
    getUnits,
    getPaymentMethod,
    getSaleEvent,
    isFetchingProjects,
    units,
    isFetchingUnits,
    getProjectPromoTags,
  } = addSalesApis

  const onChangeForProjectName = option => {
    getActiveBookingPlatforms(option?.projectId, true)
      .unwrap()
      .catch(e => notification.error({ message: e?.message }))
    updateStateManager({
      unitPrice: 0,
      dealPrice: 0,
      discountAmount: 0,
      project: option,
      featureTable: {},
    })
    form.setFieldsValue({
      unit: null,
      project: option,
      modeOfPayment: null,
      amount: null,
      cdpCommitment: option?.ProjectFinancial?.completeDownPayment,
      deadLine: null,
      saleType: null,
    })
    updateIsEditable(false)
    getUnits({ projectId: option.id })
    leadSalesInfo?.isFirstSalesTask && getProjectPromoTags(option?.id)
    getPaymentMethod({ id: option?.id, flag: flag(leadSalesInfo) })
    getSaleEvent(option?.id)
  }

  const unitOptions = useMemo(() => {
    const filteredUnits = leadSalesInfo?.isReservedWithReservedStatus
      ? units
      : units?.filter(e => e.status === "Available")
    return filteredUnits || []
  }, [leadSalesInfo, units])

  return (
    <>
      <Legend title={t("Project Details")} />
      {!leadSalesInfo?.contracts?.[0] && (
        <>
          <Group className="mbe-8" template="repeat(2, 1fr)" gap="16px 32px">
            <Form.Item className="mbe-0" required name="project" label={t("Project")} rules={rules}>
              <SelectInput
                placeholder={t("Select project")}
                options={projects}
                suffixIcon={<Text>{t("VAS")}</Text>}
                disabled={leadSalesInfo?.isReservedWithReservedStatus}
                loading={isFetchingProjects}
                getPopupContainer={false}
                onChange={(value, option) => {
                  onChangeForProjectName(option)
                }}
                extraContent={() =>
                  project?.allowProvisionalUnit && (
                    <Button
                      text={t("Request a Unit")}
                      style={{
                        margin: "15px 0 0 0",
                        height: "50px",
                        width: "150px",
                        backgroundColor: "#E6F5F9",
                        color: theme["accent-color"],
                        borderColor: "none",
                      }}
                      onClick={() => {
                        provisionalFormRef.current.setVisible(true)
                      }}
                      disabled={unit ? unit?.id !== "Provisional" : false}
                    />
                  )
                }
              />
            </Form.Item>

            <Form.Item
              className="mbe-0"
              required={!!project?.allowProvisionalUnit}
              name="unit"
              label={t("Unit")}
              rules={project?.allowProvisionalUnit ? [] : rules}
            >
              <SelectInput
                placeholder={t("Select unit")}
                disabled={
                  isFetchingUnits ||
                  stateManager?.provisionalFormFilled ||
                  !project ||
                  leadSalesInfo?.isReservedWithReservedStatus
                }
                options={unitOptions}
                inputLoading={isFetchingUnits}
                getPopupContainer={false}
                getOptionLabel={e => e.name}
                getOptionValue={e => e.id}
                allowClear
                onChange={(value, option) => {
                  form.setFieldsValue({
                    unit: option,
                    deadLine: null,
                    amount: null,
                  })
                  const fixedRows = option?.FeatureCharges?.filter(record => record?.fixed)
                  updateStateManager({
                    unit: option,
                    unitPrice: option?.price,
                    featureTable: {
                      selectedRowKeys: fixedRows?.map(record => record?.key),
                      selectedPrices: fixedRows?.reduce(
                        (acc, item) =>
                          acc +
                          (item?.amountType === "fixed" ? item?.amount : (item?.amount / 100) * option?.unitPrice),
                        0
                      ),
                      features: fixedRows,
                    },
                    dealPrice: option?.price + (option?.totalFixedFeatureCharges ?? 0),
                  })
                }}
              />
            </Form.Item>
          </Group>
          {project?.ProjectFinancial?.vasFeeEnabled && (
            <IconText
              textType="secondary"
              icon="BsInfo"
              iconProps={{
                size: "1.2em",
                color: theme["gray900"],
                hasBackground: true,
                iconContainerStyle: { padding: 0 },
              }}
              style={{ "--typography-color": theme["gray700"], fontSize: 12, gap: 8 }}
              title={
                <Text
                  style={{
                    display: "inline",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.5,
                  }}
                >
                  {t("This Project has")}{" "}
                  <span style={{ color: theme["accent-color"], fontWeight: 600 }}>{t("Bayut Service Fee (BSF)")}</span>{" "}
                  {t("enabled. BSF fee amount is required to book the unit (exclusive of due amount)")}
                </Text>
              }
            />
          )}
        </>
      )}
      <ProjectDetailBar rules={rules} />
      <ProvisionalSaleModal
        ref={provisionalFormRef}
        setParentState={updateStateManager}
        project={projects}
        projectId={project}
        parentForm={form}
      />
    </>
  )
}

export default ProjectDetails
